import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  CheckCircle, Circle, ChevronDown, ChevronUp, FileText, Home,
  Shield, Search, Scale, Key, ArrowRight, Lightbulb, MapPin, AlertTriangle,
  CreditCard, Building2, ClipboardCheck, Briefcase, Users, FileCheck
} from 'lucide-react';
import { useCountry } from '../contexts/CountryContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const SELLER_JOURNEY_STEPS = {
  UK: [
    {
      id: 'valuation',
      title: 'Get Your Property Valued',
      icon: <Home className="h-5 w-5" />,
      description: 'Understand what your property is worth before listing.',
      items: [
        { id: 'sell_epc', label: 'Order an Energy Performance Certificate (EPC) — legally required before marketing', tip: 'An EPC costs £60–£120 and is valid for 10 years. You cannot legally market your property without one. Book through a registered assessor.' },
        { id: 'sell_val', label: 'Get at least 3 valuations from local agents or use Shopperden AI Valuation', tip: 'Agents often overvalue to win your listing. Compare with Zoopla estimates and recent sold prices on the Land Registry to set a realistic price.' },
        { id: 'sell_price', label: 'Decide your asking price strategy (fixed, offers over, or guide price)', tip: 'Guide price attracts competitive offers. Fixed price is clearer. "Offers over" works well in hot markets. Your agent or Shopperden can advise.' },
      ],
    },
    {
      id: 'prepare',
      title: 'Prepare Your Property',
      icon: <ClipboardCheck className="h-5 w-5" />,
      description: 'Make your property sell faster and for more.',
      items: [
        { id: 'sell_docs', label: 'Gather title deeds, leasehold info, and any planning permissions', tip: 'Your solicitor will need these. If you have a mortgage, contact your lender for the title deeds. Leasehold? Get the management pack from your freeholder.' },
        { id: 'sell_present', label: 'Declutter, deep clean, and stage your property for viewings', tip: 'First impressions matter. Remove personal photos, fix minor repairs, and ensure good lighting. Consider professional staging for higher-value properties.' },
        { id: 'sell_photos', label: 'Get professional photos and upload 360° images to Shopperden', tip: 'Properties with professional photos sell 32% faster. Use Shopperden\'s 3D viewer for virtual tours — buyers love exploring remotely before booking viewings.' },
      ],
    },
    {
      id: 'agent_or_diy',
      title: 'Choose Agent or DIY',
      icon: <Briefcase className="h-5 w-5" />,
      description: 'Decide whether to use a Shopperden verified agent or sell yourself.',
      items: [
        { id: 'sell_agent_decide', label: 'Review Shopperden agent profiles, ratings, and fees', tip: 'Shopperden agents are verified with checked credentials. Compare their deal history, specializations, and areas covered before selecting one.' },
        { id: 'sell_agent_select', label: 'Select a verified agent OR choose to manage viewings yourself', tip: 'Agents handle marketing, viewings, and negotiations. DIY is cheaper but more work. Shopperden gives you the tools either way.' },
        { id: 'sell_list', label: 'Publish your listing on Shopperden with full details', tip: 'Include accurate room dimensions, council tax band, parking details, and nearby amenities. Honest descriptions reduce time-wasters.' },
      ],
    },
    {
      id: 'viewings_offers',
      title: 'Manage Viewings & Offers',
      icon: <MapPin className="h-5 w-5" />,
      description: 'Show your property and evaluate offers.',
      items: [
        { id: 'sell_viewings', label: 'Respond to viewing requests and schedule showings', tip: 'Be flexible with timings. Weekend viewings attract more buyers. Keep the property tidy and let natural light in. First 30 seconds form 90% of the impression.' },
        { id: 'sell_offers', label: 'Receive and evaluate offers through Shopperden', tip: 'Consider not just price but buyer position: cash buyers, chain-free, mortgage approved. A lower offer from a reliable buyer may complete faster.' },
        { id: 'sell_accept', label: 'Accept an offer and mark the deal on Shopperden', tip: 'Once accepted, both parties are "morally" committed but not legally bound until exchange. Instruct your solicitor immediately to reduce fall-through risk.' },
      ],
    },
    {
      id: 'legal_completion',
      title: 'Legal & Completion',
      icon: <Scale className="h-5 w-5" />,
      description: 'Finalise the legal process and hand over the keys.',
      items: [
        { id: 'sell_solicitor', label: 'Instruct a solicitor or conveyancer to handle the sale', tip: 'Seller\'s solicitor costs £500–£1,200 + VAT. They prepare the contract pack, respond to buyer queries, and handle the legal transfer.' },
        { id: 'sell_enquiries', label: 'Respond to buyer solicitor enquiries and searches', tip: 'Answer property information forms honestly. Delays here are the biggest cause of slow sales. Respond within 48 hours if possible.' },
        { id: 'sell_exchange', label: 'Exchange contracts — the sale is now legally binding', tip: 'At exchange, the buyer pays a deposit (usually 10%). After this, pulling out has financial penalties. Agree a completion date at this stage.' },
        { id: 'sell_complete', label: 'Complete the sale — hand over the keys', tip: 'On completion day, your solicitor receives the funds, pays off any mortgage, and transfers the title. You hand over all keys, alarm codes, and appliance manuals.' },
      ],
    },
  ],
  IN: [
    {
      id: 'valuation_in',
      title: 'Property Valuation & Documentation',
      icon: <Home className="h-5 w-5" />,
      description: 'Prepare your property documents and set the right price.',
      items: [
        { id: 'sell_in_title', label: 'Verify your property title deed and ownership records', tip: 'Get an encumbrance certificate from the sub-registrar office. This proves clear ownership with no legal disputes or mortgages pending.' },
        { id: 'sell_in_val', label: 'Get property valued — check circle rates and market rates', tip: 'Circle rate is the government minimum. Market value may be higher. Use Shopperden AI Valuation for a fair estimate based on recent sales in your area.' },
        { id: 'sell_in_rera', label: 'Check if your property/project is RERA registered', tip: 'RERA registration is mandatory for projects. Buyers increasingly check this. Having RERA details builds trust and speeds up the sale.' },
        { id: 'sell_in_tax', label: 'Calculate capital gains tax implications', tip: 'Short-term gains (under 2 years) are taxed at your slab rate. Long-term gains get indexation benefit. Consult a CA to plan tax-saving investments under Section 54.' },
      ],
    },
    {
      id: 'listing_in',
      title: 'List & Market Your Property',
      icon: <MapPin className="h-5 w-5" />,
      description: 'Create an attractive listing and reach buyers.',
      items: [
        { id: 'sell_in_photos', label: 'Upload professional photos and 360° images to Shopperden', tip: 'Properties with 360° virtual tours get 3x more enquiries. Show every room, balcony, parking, and society amenities.' },
        { id: 'sell_in_agent', label: 'Choose a Shopperden verified agent or list independently', tip: 'Agents in India charge 1-2% brokerage. Shopperden verified agents have checked RERA credentials and transparent fee structures.' },
        { id: 'sell_in_publish', label: 'Publish listing with all details (carpet area, floor, facing, amenities)', tip: 'Be specific about carpet vs built-up area. Include RERA number, maintenance charges, and possession status. Honest listings sell faster.' },
      ],
    },
    {
      id: 'deal_in',
      title: 'Negotiate & Complete Sale',
      icon: <Scale className="h-5 w-5" />,
      description: 'Finalize the deal and complete registration.',
      items: [
        { id: 'sell_in_negotiate', label: 'Evaluate offers and negotiate through Shopperden', tip: 'Consider payment mode (home loan vs cash), timeline, and token amount. A serious buyer will pay a booking amount of 1-5% upfront.' },
        { id: 'sell_in_agreement', label: 'Sign the sale agreement and collect token amount', tip: 'The agreement to sell is on stamp paper. It includes price, payment schedule, and possession date. Both parties sign with witnesses.' },
        { id: 'sell_in_register', label: 'Complete registration at the sub-registrar office', tip: 'Both buyer and seller must be present with photo ID, PAN card, and witnesses. Stamp duty (5-7%) and registration (1%) are paid by the buyer.' },
        { id: 'sell_in_handover', label: 'Hand over possession with all original documents', tip: 'Provide original title deed, society NOC, latest maintenance receipts, utility transfer forms, and all keys. Get a possession letter signed.' },
      ],
    },
  ],
  CA: [
    {
      id: 'valuation_ca',
      title: 'Prepare & Price Your Property',
      icon: <Home className="h-5 w-5" />,
      description: 'Get your home ready for the Canadian market.',
      items: [
        { id: 'sell_ca_val', label: 'Get a Comparative Market Analysis (CMA) from a REALTOR or use Shopperden AI', tip: 'A CMA compares recent sales of similar properties in your area. This is the most reliable way to price your home for the Canadian market.' },
        { id: 'sell_ca_inspect', label: 'Consider a pre-listing home inspection', tip: 'A pre-inspection ($300–$500) identifies issues before buyers find them. Fixing problems upfront avoids surprises and strengthens your negotiating position.' },
        { id: 'sell_ca_stage', label: 'Stage your property and get professional photos', tip: 'Staged homes sell 73% faster in Canada. Upload 360° images to Shopperden for virtual tours that attract out-of-province and international buyers.' },
      ],
    },
    {
      id: 'listing_ca',
      title: 'List & Show Your Property',
      icon: <MapPin className="h-5 w-5" />,
      description: 'Market your property and manage showings.',
      items: [
        { id: 'sell_ca_agent', label: 'Choose a Shopperden verified agent or list independently', tip: 'Canadian agent commissions are typically 3-5% split between buyer and seller agents. Shopperden agents are CREA/provincial board verified.' },
        { id: 'sell_ca_list', label: 'Publish your listing with full property disclosure', tip: 'Most provinces require a Seller Property Information Statement (SPIS). Honest disclosure protects you legally and builds buyer confidence.' },
        { id: 'sell_ca_show', label: 'Manage showings and open houses', tip: 'Open houses work well in competitive markets. Keep the home show-ready. Your agent handles scheduling and feedback from viewers.' },
      ],
    },
    {
      id: 'close_ca',
      title: 'Offers, Legal & Closing',
      icon: <Scale className="h-5 w-5" />,
      description: 'Accept offers and complete the sale.',
      items: [
        { id: 'sell_ca_offers', label: 'Review offers — consider price, conditions, and closing date', tip: 'Canadian offers may include conditions: financing, inspection, sale of buyer\'s home. Fewer conditions = stronger offer. Your agent advises on counter-offers.' },
        { id: 'sell_ca_lawyer', label: 'Instruct a real estate lawyer to handle the closing', tip: 'Lawyer fees: $500–$1,500. They handle title transfer, mortgage discharge, and closing adjustments (property tax, utilities).' },
        { id: 'sell_ca_close', label: 'Sign closing documents and hand over the keys', tip: 'On closing day, your lawyer receives funds, pays off your mortgage, and transfers the title. Provide all keys, garage remotes, and appliance manuals.' },
      ],
    },
  ],
};

export const JOURNEY_STEPS = {
  UK: [
    {
      id: 'solicitor',
      title: 'Instruct Your Solicitor',
      icon: <Scale className="h-5 w-5" />,
      description: 'Get legal representation sorted before anything else.',
      items: [
        { id: 'sol_id', label: 'Provide valid photo ID (passport or driving licence)', tip: 'Your solicitor needs this for anti-money laundering checks. A certified copy is usually required.' },
        { id: 'sol_address', label: 'Provide proof of address (bank statement or utility bill, last 3 months)', tip: 'Must be dated within the last 3 months. Digital statements are usually accepted.' },
        { id: 'sol_instruct', label: 'Send formal instruction letter to your chosen solicitor', tip: 'This officially appoints them to act on your behalf. Ask for a fee breakdown upfront.' },
        { id: 'sol_quote', label: 'Receive and review the solicitor fee quote', tip: 'Typical costs: £500-£1,500 + VAT. Check what is included — some charge extra for bank transfers.' },
        { id: 'sol_confirm', label: 'Confirm your solicitor details with the estate agent/seller', tip: 'The seller\'s solicitor needs your solicitor\'s contact details to send the draft contract.' },
      ],
    },
    {
      id: 'mortgage',
      title: 'Mortgage & Deposit',
      icon: <CreditCard className="h-5 w-5" />,
      description: 'Secure your financing and prepare your deposit funds.',
      items: [
        { id: 'mort_aip', label: 'Get Agreement in Principle (AIP) from a lender', tip: 'This takes 15-30 minutes online. It shows sellers you can afford the property. Valid for 60-90 days.' },
        { id: 'mort_proof', label: 'Gather proof of funds (savings statements, investments)', tip: 'The lender needs to see where your deposit is coming from. Prepare 3-6 months of bank statements.' },
        { id: 'mort_gift', label: 'If gifted deposit — get a signed gifted deposit letter', tip: 'The gift giver must confirm in writing that the money is a gift, not a loan, and they have no interest in the property.' },
        { id: 'mort_apply', label: 'Submit full mortgage application', tip: 'You\'ll need: 3 months payslips, P60, 3 months bank statements, ID, and proof of address. Self-employed? 2-3 years of accounts.' },
        { id: 'mort_offer', label: 'Receive formal mortgage offer', tip: 'This usually takes 2-4 weeks. The lender will also arrange a property valuation. Don\'t exchange contracts until you have this.' },
      ],
    },
    {
      id: 'survey',
      title: 'House Survey',
      icon: <Search className="h-5 w-5" />,
      description: 'Choose the right survey to protect your investment.',
      items: [
        { id: 'surv_choose', label: 'Choose survey type: Condition Report (£300), HomeBuyer (£400-500), or Full Building Survey (£500-700+)', tip: 'New builds: Condition Report is fine. Standard homes: HomeBuyer Report. Older/unusual properties: Full Building Survey. Never skip this step.' },
        { id: 'surv_complete', label: 'Review survey results and raise any concerns with your solicitor', tip: 'If the survey reveals issues, you can: renegotiate the price, ask the seller to fix them, or walk away. Your solicitor can advise.' },
      ],
    },
    {
      id: 'searches',
      title: 'Conveyancing Searches',
      icon: <ClipboardCheck className="h-5 w-5" />,
      description: 'Your solicitor orders these to check for hidden issues.',
      items: [
        { id: 'search_local', label: 'Local authority search (planning, building control, highways)', tip: 'Reveals: planning permissions, building regulation approvals, road schemes, conservation areas. Takes 2-6 weeks depending on the council.' },
        { id: 'search_env', label: 'Environmental search (flood risk, contamination, subsidence)', tip: 'Checks for flood zones, landfill sites, radon gas, and ground stability. Essential — your mortgage lender will require this.' },
        { id: 'search_water', label: 'Water and drainage search', tip: 'Confirms the property is connected to mains water and sewerage. Also shows if public drains run through the property.' },
      ],
    },
    {
      id: 'contracts',
      title: 'Contracts & Enquiries',
      icon: <FileText className="h-5 w-5" />,
      description: 'Review the legal documents and raise questions.',
      items: [
        { id: 'con_draft', label: 'Receive draft contract pack from seller\'s solicitor', tip: 'Includes: draft contract, property information form (TA6), fittings form (TA10), title deeds, and office copy entries.' },
        { id: 'con_title', label: 'Your solicitor checks the legal title', tip: 'They verify the seller owns the property, check for restrictions, rights of way, and any charges registered against the title.' },
        { id: 'con_ta6', label: 'Review Property Information Form (TA6)', tip: 'This covers: boundaries, disputes, planning, building work, flooding history, insurance claims. Read it carefully.' },
        { id: 'con_ta10', label: 'Review Fittings and Contents Form (TA10)', tip: 'Lists exactly what is included/excluded in the sale — curtains, carpets, light fittings, appliances, garden items.' },
        { id: 'con_enquiries', label: 'Solicitor raises pre-contract enquiries', tip: 'Questions about anything unclear in the contract pack. The seller must respond. This can take 1-4 weeks.' },
        { id: 'con_replies', label: 'Review replies to enquiries', tip: 'Your solicitor will summarise the key points. Ask about anything you don\'t understand.' },
        { id: 'con_report', label: 'Solicitor sends you a Report on Title', tip: 'A summary of everything they\'ve found. Read this carefully — it\'s your last chance to raise concerns before exchange.' },
      ],
    },
    {
      id: 'exchange',
      title: 'Exchange of Contracts',
      icon: <Shield className="h-5 w-5" />,
      description: 'The point of no return — legally binding on both sides.',
      items: [
        { id: 'ex_deposit', label: 'Transfer deposit to your solicitor (usually 10% of purchase price)', tip: 'Your solicitor holds this in their client account until exchange. Ensure the bank transfer arrives 2-3 days before exchange date.' },
        { id: 'ex_insurance', label: 'Arrange buildings insurance (effective from exchange date)', tip: 'You are responsible for the property from exchange — even if you don\'t have the keys yet. Get cover in place before exchange.' },
        { id: 'ex_sign', label: 'Sign the contract', tip: 'Your solicitor will send the contract for signing. Read the completion date, purchase price, and any special conditions.' },
        { id: 'ex_exchange', label: 'Solicitors exchange contracts by phone — it\'s now legally binding!', tip: 'After exchange, pulling out costs you the deposit (10%). The completion date is set — usually 1-4 weeks after exchange.' },
      ],
    },
    {
      id: 'completion',
      title: 'Completion',
      icon: <Key className="h-5 w-5" />,
      description: 'The finish line — you get the keys!',
      items: [
        { id: 'comp_balance', label: 'Solicitor sends balance of purchase price to seller\'s solicitor', tip: 'This happens on the morning of completion day. Your mortgage lender sends funds to your solicitor, who forwards the total.' },
        { id: 'comp_confirm', label: 'Seller\'s solicitor confirms receipt — completion is confirmed!', tip: 'Usually confirmed by early afternoon. Your solicitor will call you as soon as it\'s done.' },
        { id: 'comp_keys', label: 'Collect your keys from the estate agent or seller', tip: 'Congratulations! Take meter readings on arrival and notify utility companies of the change of ownership.' },
        { id: 'comp_sdlt', label: 'Pay Stamp Duty Land Tax (SDLT) within 14 days', tip: 'Your solicitor handles this. First-time buyers: 0% on first £425,000. Others: 0% up to £250,000, then 5% on next £675,000.' },
        { id: 'comp_register', label: 'Solicitor registers you as the new owner at HM Land Registry', tip: 'This can take a few weeks. Once registered, you\'ll receive an official copy of the title.' },
      ],
    },
  ],
  IN: [
    {
      id: 'lawyer',
      title: 'Engage a Property Lawyer',
      icon: <Scale className="h-5 w-5" />,
      description: 'Get legal representation for due diligence and documentation.',
      items: [
        { id: 'law_find', label: 'Find and instruct a property lawyer (₹10,000-₹50,000)', tip: 'Choose a lawyer experienced in property transactions in your city. Ask for references and past transaction history.' },
        { id: 'law_id', label: 'Provide KYC documents (Aadhaar, PAN card, address proof)', tip: 'Required for all property transactions in India. Keep original and photocopies ready.' },
        { id: 'law_title', label: 'Lawyer verifies title deed for last 30 years', tip: 'This is critical. The title must be clear with no disputes, mortgages, or liens. Check the chain of ownership.' },
        { id: 'law_ec', label: 'Obtain Encumbrance Certificate (EC) from sub-registrar', tip: 'Shows if the property has any legal dues or mortgages. Get it for the last 30 years ideally, minimum 13 years.' },
        { id: 'law_rera', label: 'Verify RERA registration (for under-construction projects)', tip: 'Check on your state\'s RERA website. The RERA number must match the project. Never buy an unregistered project.' },
      ],
    },
    {
      id: 'loan',
      title: 'Home Loan & Finances',
      icon: <CreditCard className="h-5 w-5" />,
      description: 'Secure your home loan and prepare down payment.',
      items: [
        { id: 'loan_compare', label: 'Compare home loan rates from SBI, HDFC, ICICI, and others', tip: 'Even 0.25% difference saves lakhs over 20 years. Check: interest rate, processing fee, prepayment charges, and tenure options.' },
        { id: 'loan_preapproval', label: 'Get home loan pre-approval letter', tip: 'Shows sellers you are a serious buyer. Most banks provide this within 3-7 days. You\'ll need: salary slips, Form 16, bank statements, IT returns.' },
        { id: 'loan_downpay', label: 'Arrange down payment (minimum 20% of property value)', tip: 'Banks finance up to 80% (sometimes 90% for smaller amounts). The rest must come from your savings. All payments must be through banking channels.' },
        { id: 'loan_sanction', label: 'Receive loan sanction letter', tip: 'The bank will verify your documents and the property. They\'ll also do an independent property valuation. This takes 1-3 weeks.' },
        { id: 'loan_docs', label: 'Submit property documents to bank for legal verification', tip: 'The bank\'s lawyer verifies the title independently. They may ask for additional documents from the seller.' },
      ],
    },
    {
      id: 'verification',
      title: 'Property Verification',
      icon: <Search className="h-5 w-5" />,
      description: 'Verify the property thoroughly before paying.',
      items: [
        { id: 'ver_visit', label: 'Physical inspection of property and surroundings', tip: 'Visit at different times of day. Check: water supply, electricity, road access, drainage, and nearby construction or nuisance.' },
        { id: 'ver_plan', label: 'Verify approved building plan with local authority', tip: 'Check if the construction matches the approved plan. Any deviation is illegal and can lead to demolition orders.' },
        { id: 'ver_oc', label: 'Check Occupancy Certificate (OC) and Completion Certificate (CC)', tip: 'Without OC, the building is technically not fit for occupation. Water and electricity connections depend on OC.' },
        { id: 'ver_society', label: 'Get Society NOC (for resale flats)', tip: 'The housing society must issue a No Objection Certificate for transfer. Check for pending society dues.' },
      ],
    },
    {
      id: 'agreement',
      title: 'Agreement & Registration',
      icon: <FileText className="h-5 w-5" />,
      description: 'Finalise the deal with proper legal documentation.',
      items: [
        { id: 'agr_negotiate', label: 'Negotiate final price and terms with seller', tip: 'Get everything in writing. Discuss: price, payment schedule, possession date, fixtures included, and any pending approvals.' },
        { id: 'agr_token', label: 'Pay token advance and sign agreement to sell', tip: 'Usually 1-5% of property value. This agreement is legally binding. Ensure all terms, timelines, and penalties are clearly mentioned.' },
        { id: 'agr_stamp', label: 'Pay stamp duty (varies by state: 3-7%)', tip: 'Maharashtra: 5-6%, Karnataka: 5%, Delhi: 4-6%, Tamil Nadu: 7%. Women buyers often get 1-2% concession. Budget for this carefully.' },
        { id: 'agr_register', label: 'Register the sale deed at sub-registrar office', tip: 'Both buyer and seller must be present with 2 witnesses. Registration charge is typically 1% of property value. Carry original documents.' },
        { id: 'agr_mutation', label: 'Apply for property mutation in local municipal records', tip: 'This transfers the property tax liability to your name. Required for future sale and utility connections.' },
      ],
    },
    {
      id: 'possession',
      title: 'Possession & Move In',
      icon: <Key className="h-5 w-5" />,
      description: 'Take possession and complete all formalities.',
      items: [
        { id: 'pos_disburse', label: 'Bank disburses final loan amount to seller', tip: 'The bank sends the remaining amount directly to the seller after all documents are verified and registered.' },
        { id: 'pos_keys', label: 'Receive possession and keys from seller', tip: 'Do a final walkthrough. Check all fittings, fixtures, and amenities are as agreed. Note meter readings.' },
        { id: 'pos_utility', label: 'Transfer utility connections to your name', tip: 'Electricity, water, gas, and piped gas connections. Carry the registered sale deed and possession letter.' },
        { id: 'pos_tax', label: 'Set up home loan tax benefits (Section 80C and Section 24)', tip: 'Claim up to ₹1.5 lakh on principal (80C) and ₹2 lakh on interest (Section 24). Get interest certificate from bank annually.' },
      ],
    },
  ],
  CA: [
    {
      id: 'preapproval',
      title: 'Mortgage Pre-Approval',
      icon: <CreditCard className="h-5 w-5" />,
      description: 'Get pre-approved before house hunting.',
      items: [
        { id: 'pre_compare', label: 'Compare rates from banks, credit unions, and mortgage brokers', tip: 'A mortgage broker can access multiple lenders. Banks offer their own products. Compare: rate, term, prepayment privileges, and portability.' },
        { id: 'pre_docs', label: 'Gather documents: T4s, NOAs, pay stubs, bank statements', tip: 'Self-employed? You\'ll need 2 years of NOAs and T1 Generals. New to Canada? Some lenders accept international income.' },
        { id: 'pre_apply', label: 'Submit pre-approval application', tip: 'The stress test requires you to qualify at your rate + 2% (or 5.25%, whichever is higher). Pre-approval is usually valid for 120 days.' },
        { id: 'pre_down', label: 'Confirm down payment source (minimum 5-20%)', tip: 'Under $500K: minimum 5%. $500K-$999K: 5% on first $500K + 10% on remainder. $1M+: minimum 20%. Less than 20% requires CMHC insurance.' },
      ],
    },
    {
      id: 'offer',
      title: 'Making an Offer',
      icon: <FileText className="h-5 w-5" />,
      description: 'Draft and submit your purchase offer.',
      items: [
        { id: 'off_lawyer', label: 'Hire a real estate lawyer ($500-$2,000)', tip: 'Essential for reviewing the agreement, conducting title searches, and handling closing. Get a quote upfront including disbursements.' },
        { id: 'off_draft', label: 'Draft the offer with conditions (financing, inspection, review)', tip: 'Standard conditions: financing (5 days), home inspection (7 days), status certificate review (for condos). Your lawyer can help draft.' },
        { id: 'off_submit', label: 'Submit the offer with deposit cheque', tip: 'Deposit is typically 5% of purchase price, held in trust. If the offer falls through due to conditions, you get the deposit back.' },
        { id: 'off_negotiate', label: 'Negotiate and reach agreement on price and terms', tip: 'In a buyer\'s market, you can negotiate 5-10% below asking. In a seller\'s market, you may need to offer at or above asking.' },
      ],
    },
    {
      id: 'inspection',
      title: 'Home Inspection',
      icon: <Search className="h-5 w-5" />,
      description: 'Inspect the property before removing conditions.',
      items: [
        { id: 'insp_hire', label: 'Hire a certified home inspector ($300-$600)', tip: 'Choose an inspector certified by CAHPI or InterNACHI. Ask for a sample report. Attend the inspection in person if possible.' },
        { id: 'insp_review', label: 'Review inspection report and decide: proceed, renegotiate, or walk away', tip: 'Major concerns: foundation issues, roof damage, mould, electrical problems, plumbing leaks. Minor issues are normal in any home.' },
        { id: 'insp_waive', label: 'Waive or fulfil the inspection condition', tip: 'If satisfied, you waive the condition and the deal moves forward. If not, you can negotiate repairs, price reduction, or cancel.' },
      ],
    },
    {
      id: 'financing',
      title: 'Finalise Financing',
      icon: <Building2 className="h-5 w-5" />,
      description: 'Convert pre-approval to firm mortgage commitment.',
      items: [
        { id: 'fin_appraisal', label: 'Lender orders property appraisal', tip: 'The lender needs to confirm the property is worth the purchase price. If it appraises lower, you may need a larger down payment.' },
        { id: 'fin_commit', label: 'Receive firm mortgage commitment', tip: 'Once the appraisal is approved and all conditions met, the lender issues a firm commitment. This is your green light.' },
        { id: 'fin_insurance', label: 'Arrange home insurance (required before closing)', tip: 'Lenders require proof of insurance before closing. Get quotes from multiple providers. Coverage should start on closing day.' },
        { id: 'fin_waive', label: 'Waive the financing condition', tip: 'With your firm mortgage commitment in hand, you waive the financing condition. The deal is now firm.' },
      ],
    },
    {
      id: 'closing',
      title: 'Closing Day',
      icon: <Key className="h-5 w-5" />,
      description: 'The final step — you become a homeowner!',
      items: [
        { id: 'close_review', label: 'Lawyer reviews closing documents and statement of adjustments', tip: 'The statement shows: purchase price, deposit, mortgage amount, land transfer tax, legal fees, and your balance due on closing.' },
        { id: 'close_ltt', label: 'Pay land transfer tax (varies by province)', tip: 'Ontario: 0.5-2.5% + Toronto municipal LTT. BC: 1-3%. First-time buyers may qualify for rebates. Your lawyer calculates the exact amount.' },
        { id: 'close_sign', label: 'Sign mortgage and transfer documents', tip: 'Your lawyer will walk you through each document. Bring valid photo ID. This usually takes 30-60 minutes.' },
        { id: 'close_funds', label: 'Transfer closing funds to your lawyer\'s trust account', tip: 'Your lawyer will tell you the exact amount 2-3 days before closing. Use a bank draft or wire transfer — no personal cheques.' },
        { id: 'close_keys', label: 'Receive the keys — congratulations, you\'re a homeowner!', tip: 'Keys are usually available by late afternoon on closing day. Take photos of meter readings and check that all agreed items are present.' },
      ],
    },
  ],
};

// LANDLORD JOURNEY STEPS (for rent listings)
export const LANDLORD_JOURNEY_STEPS = {
  UK: [
    { id: 'compliance', title: 'Landlord Compliance', icon: <Shield className="h-5 w-5" />, description: 'Ensure your property meets all UK letting requirements.', items: [
      { id: 'epc', label: 'Get an Energy Performance Certificate (EPC) — rating E or above', tip: 'You cannot legally let a property with an EPC rating below E. Get one from an accredited assessor (costs £60-120).' },
      { id: 'gas_safety', label: 'Gas Safety Certificate (CP12) — annual requirement', tip: 'A Gas Safe registered engineer must inspect all gas appliances annually. Keep records for 2 years.' },
      { id: 'electrical', label: 'Electrical Installation Condition Report (EICR)', tip: 'Required every 5 years. Must be satisfactory before tenants move in.' },
      { id: 'smoke_co', label: 'Smoke and CO alarms fitted on every floor', tip: 'Required by law since October 2015. CO alarms needed where there is a combustion appliance.' },
      { id: 'how_to_rent', label: 'Provide tenant with "How to Rent" guide', tip: 'You must give tenants the latest government "How to Rent" checklist before or at the start of the tenancy.' },
    ]},
    { id: 'listing', title: 'List Your Rental Property', icon: <Home className="h-5 w-5" />, description: 'Create an attractive listing to find quality tenants.', items: [
      { id: 'photos', label: 'Take professional photos of every room', tip: 'Good photos attract 3x more enquiries. Use natural light and declutter before photographing.' },
      { id: 'pricing', label: 'Set competitive monthly rent (check local comparables)', tip: 'Use the Market Trends tool in your dashboard to check comparable rents in your area.' },
      { id: 'description', label: 'Write a clear, honest description', tip: 'Include: number of rooms, parking, garden, nearest station, council tax band, bills included or not.' },
      { id: 'publish', label: 'Publish your listing on Shopperden', tip: 'Once published, verified tenants can view, book viewings, and apply.' },
    ]},
    { id: 'tenant_find', title: 'Find & Vet Tenants', icon: <Users className="h-5 w-5" />, description: 'Review applications and choose the right tenant.', items: [
      { id: 'viewings', label: 'Conduct viewings with interested tenants', tip: 'Allow at least 15 minutes per viewing. Be prepared to answer questions about bills, neighbours, and maintenance.' },
      { id: 'references', label: 'Run reference and credit checks', tip: 'Check employment, previous landlord reference, and credit score. Services cost £20-30 per applicant.' },
      { id: 'right_to_rent', label: 'Verify Right to Rent (UK immigration check)', tip: 'You must check all adult tenants have the legal right to rent in England before the tenancy starts. Fines up to £3,000 per tenant.' },
      { id: 'select', label: 'Select your tenant and agree terms', tip: 'Confirm: monthly rent, deposit amount, move-in date, tenancy length, and any special conditions.' },
    ]},
    { id: 'tenancy_start', title: 'Start the Tenancy', icon: <Key className="h-5 w-5" />, description: 'Protect the deposit and hand over the keys.', items: [
      { id: 'deposit', label: 'Protect deposit in a government scheme within 30 days', tip: 'You MUST protect the deposit in DPS, MyDeposits, or TDS. Failure to protect means you cannot serve a Section 21 notice.' },
      { id: 'inventory', label: 'Complete a full inventory with photos', tip: 'Document every room, appliance, and item. Both parties sign. This protects you at the end of the tenancy.' },
      { id: 'agreement', label: 'Sign the Assured Shorthold Tenancy (AST) agreement', tip: 'Use a solicitor-drafted AST. Include: rent amount, payment date, deposit, break clause, maintenance responsibilities.' },
      { id: 'keys', label: 'Hand over the keys and meter readings', tip: 'Record all meter readings together. Notify utility companies and council of the change.' },
    ]},
  ],
  India: [
    { id: 'compliance', title: 'Property Compliance', icon: <Shield className="h-5 w-5" />, description: 'Ensure your property meets Indian rental regulations.', items: [
      { id: 'ownership', label: 'Verify property ownership documents', tip: 'Keep sale deed, property tax receipts, and society NOC ready.' },
      { id: 'society_noc', label: 'Get Society/Association NOC for renting', tip: 'Most housing societies require written consent before you can rent out.' },
      { id: 'police', label: 'Police verification of tenant (mandatory in many states)', tip: 'Required in Maharashtra, Delhi, Karnataka. Submit at local police station.' },
    ]},
    { id: 'listing', title: 'List Your Rental', icon: <Home className="h-5 w-5" />, description: 'Create your rental listing.', items: [
      { id: 'photos', label: 'Photograph the property', tip: 'Cover all rooms, balcony, parking, and amenities.' },
      { id: 'pricing', label: 'Set monthly rent and security deposit', tip: 'Security deposit is typically 2-3 months rent in most Indian cities.' },
      { id: 'publish', label: 'Publish on Shopperden', tip: 'Your listing will be visible to verified tenants.' },
    ]},
    { id: 'agreement', title: 'Rental Agreement', icon: <FileText className="h-5 w-5" />, description: 'Execute a legally binding rental agreement.', items: [
      { id: 'draft', label: 'Draft rent agreement (11 months standard)', tip: '11-month agreements do not require registration, keeping costs low.' },
      { id: 'stamp', label: 'Pay stamp duty and get it notarised', tip: 'Stamp duty varies by state. Maharashtra: 0.25% of annual rent or ₹500.' },
      { id: 'register', label: 'Register agreement (if >11 months)', tip: 'Agreements over 11 months must be registered at Sub-Registrar office.' },
    ]},
  ],
  Canada: [
    { id: 'compliance', title: 'Landlord Obligations', icon: <Shield className="h-5 w-5" />, description: 'Meet provincial landlord requirements.', items: [
      { id: 'standards', label: 'Ensure property meets housing standards', tip: 'Provincial housing standards vary. Common requirements: working heating, plumbing, electrical, and structural safety.' },
      { id: 'fire', label: 'Install smoke and CO detectors', tip: 'Required on every level. Test monthly and replace batteries annually.' },
      { id: 'rtb', label: 'Register with Residential Tenancies Board (if required)', tip: 'Ontario: register with the Landlord and Tenant Board. BC: register with the Residential Tenancy Branch.' },
    ]},
    { id: 'listing', title: 'List Your Rental', icon: <Home className="h-5 w-5" />, description: 'Find quality tenants.', items: [
      { id: 'photos', label: 'Professional photos and virtual tour', tip: 'Include all rooms, parking, storage, laundry facilities.' },
      { id: 'pricing', label: 'Set rent (check provincial rent control rules)', tip: 'Ontario: annual rent increases capped at guideline percentage. BC: similar rules. Alberta: no rent control.' },
      { id: 'publish', label: 'Publish on Shopperden', tip: 'Reach verified tenants across Canada.' },
    ]},
    { id: 'lease', title: 'Execute the Lease', icon: <FileText className="h-5 w-5" />, description: 'Sign a standard lease and protect both parties.', items: [
      { id: 'standard', label: 'Use provincial standard lease form', tip: 'Ontario requires the Standard Lease form. BC uses the RTB-1 form. Using the standard form is mandatory.' },
      { id: 'deposit', label: 'Collect security deposit (rules vary by province)', tip: 'Ontario: last month rent only (no damage deposit). BC: half month rent maximum. Alberta: one month maximum.' },
      { id: 'movein', label: 'Complete move-in inspection together', tip: 'Document the condition of everything. Both sign the inspection report. Take dated photos.' },
    ]},
  ],
};

// TENANT RENTING JOURNEY STEPS
export const TENANT_JOURNEY_STEPS = {
  UK: [
    { id: 'search', title: 'Search for Your Rental', icon: <Search className="h-5 w-5" />, description: 'Find the right rental property for your needs.', items: [
      { id: 'budget', label: 'Calculate your monthly budget (rent + bills + council tax)', tip: 'Rule of thumb: rent should be no more than 35% of your take-home pay. Add £150-250/month for bills.' },
      { id: 'search', label: 'Search properties on Shopperden', tip: 'Filter by location, price, bedrooms, and property type. Save your favourites.' },
      { id: 'viewings', label: 'Book viewings for shortlisted properties', tip: 'Visit at different times of day. Check noise levels, parking, and local amenities.' },
    ]},
    { id: 'apply', title: 'Apply & Get Approved', icon: <FileCheck className="h-5 w-5" />, description: 'Submit your application and pass referencing.', items: [
      { id: 'apply', label: 'Submit rental application', tip: 'Have these ready: proof of income (3 months payslips), bank statements, photo ID, and previous landlord details.' },
      { id: 'references', label: 'Pass credit and reference checks', tip: 'The landlord or agent will check your credit score, employment, and previous tenancy history.' },
      { id: 'guarantor', label: 'Provide a guarantor if required', tip: 'Guarantors are often required for first-time renters or those earning under 2.5x the annual rent.' },
    ]},
    { id: 'move_in', title: 'Move In', icon: <Key className="h-5 w-5" />, description: 'Sign the agreement and collect your keys.', items: [
      { id: 'agreement', label: 'Sign the tenancy agreement (AST)', tip: 'Read every clause carefully. Check: rent amount, deposit, break clause, notice period, and maintenance responsibilities.' },
      { id: 'deposit', label: 'Pay deposit and first month rent', tip: 'Deposit is usually 5 weeks rent (capped by Tenant Fees Act 2019). You should receive deposit protection details within 30 days.' },
      { id: 'inventory', label: 'Complete inventory check together', tip: 'Go through every room with the landlord. Note any existing damage. Take your own photos with timestamps.' },
      { id: 'keys', label: 'Collect keys and take meter readings', tip: 'Set up utilities in your name. Register for council tax. Redirect your mail.' },
    ]},
  ],
  India: [
    { id: 'search', title: 'Find Your Rental', icon: <Search className="h-5 w-5" />, description: 'Search for the right property.', items: [
      { id: 'budget', label: 'Set budget (rent + maintenance + deposit)', tip: 'Security deposit is typically 2-3 months rent. Monthly maintenance charges vary by society.' },
      { id: 'search', label: 'Search on Shopperden with filters', tip: 'Filter by BHK type, furnished/unfurnished, and locality.' },
      { id: 'viewings', label: 'Visit shortlisted properties', tip: 'Check water supply timing, power backup, lift availability, and parking.' },
    ]},
    { id: 'agreement', title: 'Rental Agreement', icon: <FileText className="h-5 w-5" />, description: 'Finalise terms and sign the agreement.', items: [
      { id: 'negotiate', label: 'Negotiate rent and terms', tip: 'Standard terms: 5-10% annual rent increase, 1-2 months notice period, painting charges at exit.' },
      { id: 'agreement', label: 'Sign 11-month rent agreement', tip: 'Get it notarised. Both parties keep a copy. Include: rent, deposit, maintenance split, lock-in period.' },
      { id: 'police', label: 'Complete police verification', tip: 'Submit ID proof and passport photos at local police station. Some cities allow online submission.' },
    ]},
  ],
  Canada: [
    { id: 'search', title: 'Find Your Rental', icon: <Search className="h-5 w-5" />, description: 'Search for available properties.', items: [
      { id: 'budget', label: 'Calculate budget (rent + utilities + insurance)', tip: 'Tenant insurance costs $20-40/month and is often required by landlords.' },
      { id: 'search', label: 'Search on Shopperden', tip: 'Filter by city, price, bedrooms, and pet-friendly options.' },
      { id: 'viewings', label: 'Book and attend viewings', tip: 'Check: laundry, parking, storage, heating type, and included utilities.' },
    ]},
    { id: 'lease', title: 'Sign the Lease', icon: <FileText className="h-5 w-5" />, description: 'Review and sign the standard lease.', items: [
      { id: 'review', label: 'Review the standard lease form', tip: 'Provinces require standard lease forms. Read all clauses about rent increases, maintenance, and notice periods.' },
      { id: 'deposit', label: 'Pay deposit and first/last month rent', tip: 'Ontario: last month rent required upfront. BC: half month security deposit. Alberta: one month maximum.' },
      { id: 'insurance', label: 'Get tenant insurance', tip: 'Covers your belongings, liability, and additional living expenses. Shop around for best rates.' },
      { id: 'movein', label: 'Complete move-in inspection', tip: 'Document everything with photos. Note existing damage. Both parties sign the inspection form.' },
    ]},
  ],
};

const BuyerJourney = ({ userRole = 'buyer' }) => {
  const { country } = useCountry();
  const [checkedItems, setCheckedItems] = useState({});
  const [expandedStep, setExpandedStep] = useState(null);
  const [showTip, setShowTip] = useState(null);
  const [loading, setLoading] = useState(true);

  const stepsSource = (() => {
    if (userRole === 'seller') return SELLER_JOURNEY_STEPS;
    if (userRole === 'landlord') return LANDLORD_JOURNEY_STEPS;
    if (userRole === 'tenant') return TENANT_JOURNEY_STEPS;
    return JOURNEY_STEPS;
  })();
  const steps = stepsSource[country] || stepsSource.UK;

  // Load saved progress
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/buyer-journey/progress`);
        if (res.data && res.data.checked_items) {
          setCheckedItems(res.data.checked_items);
        }
      } catch { /* first time user */ }
      setLoading(false);
    };
    loadProgress();
  }, []);

  // Save progress
  const saveProgress = async (updated) => {
    try {
      await axios.post(`${API_URL}/api/buyer-journey/progress`, { checked_items: updated, country });
    } catch { /* silent */ }
  };

  const toggleItem = (itemId) => {
    const updated = { ...checkedItems, [itemId]: !checkedItems[itemId] };
    setCheckedItems(updated);
    saveProgress(updated);
  };

  const getStepProgress = (step) => {
    const checked = step.items.filter(i => checkedItems[i.id]).length;
    return { checked, total: step.items.length, percent: Math.round((checked / step.items.length) * 100) };
  };

  const totalChecked = steps.reduce((acc, s) => acc + s.items.filter(i => checkedItems[i.id]).length, 0);
  const totalItems = steps.reduce((acc, s) => acc + s.items.length, 0);
  const overallPercent = Math.round((totalChecked / totalItems) * 100);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="buyer-journey">
      {/* Header & Progress */}
      <div className="luxury-card p-6 mb-6" data-testid="journey-header">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-1">
              Your {{ seller: 'Selling', landlord: 'Landlord', tenant: 'Renting', buyer: 'Buying' }[userRole] || 'Property'} Journey
              <span className="text-sm ml-2 font-sans text-[#E07A3A] uppercase tracking-wider font-semibold">
                {country === 'IN' ? 'India' : country === 'CA' ? 'Canada' : 'United Kingdom'}
              </span>
            </h2>
            <p className="text-sm text-slate-500">
              Follow each step in order. Tap any item to mark it complete. Tap the lightbulb for expert guidance.
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-3xl font-bold text-[#0F172A]">{overallPercent}%</p>
            <p className="text-xs text-slate-400">{totalChecked} of {totalItems} complete</p>
          </div>
        </div>
        {/* Overall Progress Bar */}
        <div className="mt-4 h-2 bg-slate-100 overflow-hidden">
          <div
            className="h-full bg-[#E07A3A] transition-all duration-500"
            style={{ width: `${overallPercent}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map((step, idx) => {
          const progress = getStepProgress(step);
          const isExpanded = expandedStep === step.id;
          const isComplete = progress.percent === 100;

          return (
            <div key={step.id} className={`luxury-card overflow-hidden ${isComplete ? 'border-emerald-200' : ''}`} data-testid={`step-${step.id}`}>
              {/* Step Header */}
              <div
                className="p-4 sm:p-5 cursor-pointer flex items-center gap-4"
                onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                data-testid={`step-header-${step.id}`}
              >
                <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-emerald-500 text-white' : 'bg-[#0F172A] text-white'}`}>
                  {isComplete ? <CheckCircle className="h-5 w-5" /> : <span className="text-sm font-bold">{idx + 1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-semibold text-sm sm:text-base ${isComplete ? 'text-emerald-700' : 'text-[#0F172A]'}`}>{step.title}</h3>
                  <p className="text-xs text-slate-400 hidden sm:block">{step.description}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <span className={`text-sm font-semibold ${isComplete ? 'text-emerald-600' : 'text-slate-500'}`}>{progress.checked}/{progress.total}</span>
                  </div>
                  <div className="w-16 h-1.5 bg-slate-100 hidden sm:block">
                    <div className={`h-full transition-all duration-300 ${isComplete ? 'bg-emerald-500' : 'bg-[#E07A3A]'}`} style={{ width: `${progress.percent}%` }} />
                  </div>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </div>
              </div>

              {/* Expanded Items */}
              {isExpanded && (
                <div className="border-t border-slate-100 p-4 sm:p-5 space-y-2" data-testid={`step-items-${step.id}`}>
                  {step.items.map((item) => (
                    <div key={item.id} className="group">
                      <div className="flex items-start gap-3 py-2">
                        <button
                          onClick={() => toggleItem(item.id)}
                          className="flex-shrink-0 mt-0.5"
                          data-testid={`check-${item.id}`}
                        >
                          {checkedItems[item.id] ? (
                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                          ) : (
                            <Circle className="h-5 w-5 text-slate-300 hover:text-[#E07A3A] transition-colors" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-relaxed ${checkedItems[item.id] ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                            {item.label}
                          </p>
                        </div>
                        <button
                          onClick={() => setShowTip(showTip === item.id ? null : item.id)}
                          className={`flex-shrink-0 p-1 transition-colors ${showTip === item.id ? 'text-[#E07A3A]' : 'text-slate-300 hover:text-[#E07A3A]'}`}
                          data-testid={`tip-${item.id}`}
                        >
                          <Lightbulb className="h-4 w-4" />
                        </button>
                      </div>
                      {/* AI Tip */}
                      {showTip === item.id && (
                        <div className="ml-8 mb-2 p-3 bg-amber-50 border border-amber-100 text-sm text-amber-800 leading-relaxed" data-testid={`tip-content-${item.id}`}>
                          <span className="font-semibold text-[#E07A3A] text-xs uppercase tracking-wider block mb-1">Shopperden Guidance</span>
                          {item.tip}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Completion Message */}
      {overallPercent === 100 && (
        <div className="luxury-card p-8 text-center mt-6 bg-emerald-50 border-emerald-200" data-testid="journey-complete">
          <Key className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="font-['Playfair_Display'] text-2xl text-emerald-700 mb-2">Congratulations!</h3>
          <p className="text-sm text-emerald-600">
            {{ 
              seller: 'You have completed all steps in your property selling journey. Your sale is complete!',
              landlord: 'You have completed all steps in your landlord journey. Your property is tenanted!',
              tenant: 'You have completed all steps in your renting journey. Welcome to your new home!',
              buyer: 'You have completed all steps in your property buying journey. Welcome to your new home!',
            }[userRole] || 'You have completed all steps. Well done!'}
          </p>
        </div>
      )}

      {/* Need Help Banner */}
      <div className="mt-6 bg-[#0F172A] p-5 flex flex-col sm:flex-row items-center gap-4" data-testid="need-help-banner">
        <Briefcase className="h-8 w-8 text-[#E07A3A] flex-shrink-0" />
        <div className="flex-1 text-center sm:text-left">
          <p className="text-white text-sm font-semibold">Need professional help with any step?</p>
          <p className="text-white/50 text-xs">
            {userRole === 'tenant' || userRole === 'landlord'
              ? 'Browse our directory of letting agents, landlord advisors, and inventory clerks.'
              : 'Browse our directory of solicitors, surveyors, and mortgage advisors.'}
          </p>
        </div>
        <a href={userRole === 'tenant' ? '/tenant-renting-guide' : '/mortgage-guide'}>
          <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E] text-xs tracking-widest uppercase font-semibold px-6 py-4" data-testid="find-pro-btn">
            {userRole === 'tenant' ? 'Renting Guide' : 'Find Professionals'} <ArrowRight className="ml-2 h-3.5 w-3.5" />
          </Button>
        </a>
      </div>
    </div>
  );
};

export default BuyerJourney;
